import { atom, RecoilValueReadOnly, selector, selectorFamily } from 'recoil'
import { keyFactory } from 'src/utils/recoil'
import {
  AMCensusBlockGroup,
  AMCensusBlockValue,
  AMH3Shape,
  amMobilityProvidersEnum,
  AMZipCode,
  BoundaryType,
  RGBAColor,
  VisitorOriginType,
  YYYYMMDDDateString,
} from 'src/common-services'
import { alphamapClient } from 'src/clients'
import { getMapColorsByIndex, MIXED } from 'src/utils/map-colors-by-percent'
import { ExtendedShapeValue } from 'src/atoms/true-trade-area/visitors-origin'
import { rgbaColorToStr } from 'src/utils/colors'
import { VisitsItem } from 'src/types/true-trade-area'
import { visitsToVisitsShape } from 'src/utils/true-trade-area'
import { overTimeVisits } from 'src/atoms/true-trade-area/change-over-time'
import { FeatureWithColor } from 'src/components/map-layers/utils'
import { TTA_DEMO_COMPARE_COLORS, TTA_DEMO_OVERLAP_COLOR } from 'src/components/theme/colors'
import { uniqBy } from 'lodash'
import { ttareademographicsData } from 'src/components/insights/atoms'
import { h3ToGeoBoundary } from 'h3-js'
import turf from 'src/utils/turf'
import { Feature, Point, Polygon } from '@turf/helpers'
import { MobilityDateRange } from 'src/atoms/mobility'
import subMonths from 'date-fns/subMonths'

import * as mobilityAtoms from 'src/atoms/mobility'
import { convertDateToString } from 'src/utils/date-utils'

const key = keyFactory('tta-overview')

export const availableBoundariesByLocationsId = selectorFamily<BoundaryType[], string>({
  key: key('has-h3-boundary-type-by-id'),
  get: (locationId) => async () => {
    const availableBoundaries = await alphamapClient.mobility.getAvailableBoundaries(locationId)
    return availableBoundaries
  },
})

export const availableBoundaries = selector({
  key: key('available-boundaries'),
  get: async ({ get }) => {
    const locationId = get(mobilityAtoms.alphamapLocationId)
    if (!locationId) {
      return []
    }
    return get(availableBoundariesByLocationsId(locationId))
  },
})

export const defaultBoundaryType = selector<BoundaryType>({
  key: key('default-boundary-type'),
  get: ({ get }) => {
    const available = get(availableBoundaries)

    if (available.length === 0 || available.includes('h3')) {
      return 'h3'
    }
    if (available.includes('cbg')) {
      return 'cbg'
    }
    return 'zipcode'
  },
})

export const boundaryType = atom<BoundaryType>({
  key: key('boundary-type'),
  default: defaultBoundaryType,
})

export const validTTADateRange = selector<MobilityDateRange>({
  key: key('valid-tta-date-range'),
  get: async ({ get }) => {
    const locationId = get(mobilityAtoms.alphamapLocationId)
    if (!locationId) {
      return get(mobilityAtoms.validDateRange)
    }

    const boundary = get(boundaryType)
    return get(validTTADateRangeByParams({ locationId, boundaryType: boundary }))
  },
})

export const validTTADateRangeByParams = selectorFamily<
  MobilityDateRange,
  { locationId: string; boundaryType: BoundaryType }
>({
  key: key('valid-tta-date-range-by-params'),
  get:
    ({ locationId, boundaryType }) =>
    async ({ get }) => {
      const boundary = boundaryType
      // i.e. h3 uses the 'new' uncast vdp stuff
      // cbg and zipcode use the 'old' uncast ml stuff
      const provider = boundary === 'h3' ? amMobilityProvidersEnum.unacast_vdp : amMobilityProvidersEnum.unacast_ml

      return get(
        mobilityAtoms.validTTADateRangeById({
          id: locationId,
          provider,
        })
      )
    },
})

const defaultStartDate = selector<YYYYMMDDDateString>({
  key: key('default-start-date'),
  get: ({ get }) => {
    const { end } = get(validTTADateRange)
    const date = subMonths(end, 3)

    return convertDateToString(date)
  },
})

export const startDate = atom<YYYYMMDDDateString | undefined>({
  key: key('start-date'),
  default: defaultStartDate,
})

const defaultEndDate = selector<YYYYMMDDDateString>({
  key: key('default-end-date'),
  get: ({ get }) => {
    const { end } = get(validTTADateRange)
    return convertDateToString(end)
  },
})

export const endDate = atom<YYYYMMDDDateString | undefined>({
  key: key('end-date'),
  default: defaultEndDate,
})

export const visitorOrigin = atom<VisitorOriginType>({
  key: key('visitor-origin'),
  default: 'home',
})

const overviewVLocationVisits = selector<ExtendedShapeValue[]>({
  key: key('overview-location-visits'),
  get: async ({ get }) => {
    const locationId = get(mobilityAtoms.alphamapLocationId)
    if (!locationId) {
      return []
    }
    const validDates = get(mobilityAtoms.validDateRange)
    if (!validDates) {
      return []
    }
    const start = get(startDate)
    const end = get(endDate)
    if (!start || !end) {
      return []
    }

    const type = get(boundaryType)
    const _visitorOrigin = get(visitorOrigin)

    const visitsResult = await alphamapClient.mobility.getLocationVisitsByHomeDateRange({
      startDate: start,
      endDate: end,
      id: locationId,
      type,
      visitorOrigin: _visitorOrigin,
    })

    const visitsMap: Record<string, AMCensusBlockValue> = {}
    visitsResult.forEach((item) => {
      item.visitorsByHome.forEach((visitor) => {
        const existing = visitsMap[visitor.id]
        if (existing) {
          existing.value += visitor.value
        } else {
          visitsMap[visitor.id] = visitor
        }
      })
    })

    const list = Object.keys(visitsMap)
      .map((key) => visitsMap[key])
      .sort((a, b) => b.value - a.value)

    return visitsToVisitsShape(list)
  },
})

// Compare locations
export const compareLocations = atom<mobilityAtoms.AMMobilityLocation[]>({
  key: key('compare-locations'),
  default: [],
})

export const compareLocationsVisits = selector<ExtendedShapeValue[][]>({
  key: key('compare-locations-visits'),
  get: async ({ get }) => {
    const locations = get(compareLocations)
    const type = get(boundaryType)
    const validDates = get(mobilityAtoms.validDateRange)
    if (!validDates) {
      return []
    }
    const start = get(startDate)
    const end = get(endDate)
    if (!start || !end) {
      return []
    }

    const _visitorOrigin = get(visitorOrigin)
    const results = []
    for (const location of locations) {
      const visitsResult = await alphamapClient.mobility.getLocationVisitsByHomeDateRange({
        startDate: start,
        endDate: end,
        id: location.id,
        type,
        visitorOrigin: _visitorOrigin,
      })
      const visitsMap: Record<string, AMCensusBlockValue> = {}
      visitsResult.forEach((item) => {
        item.visitorsByHome.forEach((visitor) => {
          const existing = visitsMap[visitor.id]
          if (existing) {
            existing.value += visitor.value
          } else {
            visitsMap[visitor.id] = visitor
          }
        })
      })
      const list = Object.keys(visitsMap).map((key) => visitsMap[key])
      results.push(visitsToVisitsShape(list))
    }
    return results
  },
})

export const bucketing = selectorFamily({
  key: key('bucketing'),
  get:
    (locationsAtom: RecoilValueReadOnly<ExtendedShapeValue[]>) =>
    ({ get }) => {
      const locations = get(locationsAtom)
      return createBuckets(locations)
    },
})

export const NUM_OF_TTA_BUCKETS = 10
export const locationVisits = selectorFamily<VisitsItem[], boolean>({
  key: key('location-visits'),
  get:
    (overTime = false) =>
    async ({ get }) => {
      const visitsAtom = overTime ? overTimeVisits : overviewVLocationVisits
      const locations = get(visitsAtom)

      const items: VisitsItem[] = locations.map((location, index) => {
        const bucketColorIndex = Math.floor((NUM_OF_TTA_BUCKETS * index || 1) / locations.length)
        const bucketColor = MIXED[bucketColorIndex]
        return {
          id: location?.id,
          value: location?.percentTotal || 0,
          ...bucketColor,
        }
      })
      return items
    },
})

export const locationIds = selectorFamily<string[], boolean>({
  key: key('location-ids'),
  get:
    (overTime = false) =>
    async ({ get }) => {
      const locations = get(locationVisits(overTime))
      const locationIds = locations.map((location) => location.id)
      return locationIds
    },
})

export const locationVisitsColors = selectorFamily<{ fillColors: string[]; lineColors: string[] }, boolean>({
  key: key('location-visits-colors'),
  get:
    (overTime = false) =>
    async ({ get }) => {
      const visits = get(locationVisits(overTime))
      const fillColors: string[] = []
      const lineColors: string[] = []
      visits.forEach((shape) => {
        if (!shape.id) {
          return
        }
        fillColors.push(shape.id as string)
        lineColors.push(shape.id as string)

        fillColors.push(rgbaColorToStr(shape.color ?? ([0, 0, 0, 0] as RGBAColor)))
        lineColors.push(rgbaColorToStr(shape.lineColor ?? ([0, 0, 0, 0] as RGBAColor)))
      })
      return { fillColors, lineColors }
    },
})

export const locationVisitsMap = selectorFamily<Record<string, VisitsItem>, boolean>({
  key: key('location-visits-map'),
  get:
    (overTime = false) =>
    async ({ get }) => {
      const visits = get(locationVisits(overTime))
      const map: Record<string, VisitsItem> = {}

      visits.forEach((shape) => {
        if (!shape.id) {
          return
        }
        map[shape.id] = shape
      })
      return map
    },
})

// Used to render the map as static image when converting the widget to an image
export const isMapStatic = atom<boolean>({
  key: key('is-map-static'),
  default: false,
})

export const getVisitsShapes = async (ids: string[], boundaryType: BoundaryType) => {
  let shapes: Array<AMCensusBlockGroup | AMZipCode | AMH3Shape> = []

  switch (boundaryType) {
    case 'cbg':
      shapes = await alphamapClient.shapes.getCensusBlockGroups({
        ids,
      })
      break
    case 'zipcode':
      shapes = await alphamapClient.shapes.getZipCodes({
        ids,
      })
      break
    case 'h3':
      // convert h3 ids to polygons
      shapes = ids.map((id) => {
        const boundary = h3ToGeoBoundary(id, true) // closed loop boundary
        const polygon = turf.polygon([boundary])
        return {
          id,
          geometry: polygon.geometry,
        }
      })
      break
    default:
      throw new Error(`Invalid boundary type: ${boundaryType}`)
  }

  return shapes
}

// Primary location visits shapes
export const locationVisitsShapes = selector<FeatureWithColor[]>({
  key: key('location-visits-shapes'),
  get: async ({ get }) => {
    const ids = get(locationIds(false))
    const primaryLocation = get(mobilityAtoms.alphamapLocation)
    if (!primaryLocation) {
      return []
    }

    const locations = get(locationVisits(false))
    const _compareLocations = get(compareLocations)
    const _boundaryType = get(boundaryType)

    const shapes = await getVisitsShapes(ids, _boundaryType)

    const features: Array<FeatureWithColor | undefined> = locations.map((location) => {
      const shape = shapes.find((shape) => shape.id === location.id)
      if (!shape) {
        return undefined
      }

      // If there are compare locations, use the first mobility color else use the location's colors
      const locationColors = _compareLocations.length
        ? {
            fillColor: TTA_DEMO_COMPARE_COLORS[0],
            lineColor: TTA_DEMO_COMPARE_COLORS[0],
          }
        : {
            fillColor: rgbaColorToStr(location.fillColor ?? ([0, 0, 0, 0] as RGBAColor)),
            lineColor: rgbaColorToStr(location.lineColor ?? ([0, 0, 0, 0] as RGBAColor)),
          }

      const feature: FeatureWithColor = {
        id: shape.id,
        geometry: shape.geometry,
        type: 'Feature',
        properties: {
          ...location,
          ...locationColors,
          locationIndex: 0,
          name: primaryLocation?.name,
          locationId: primaryLocation?.id,
        },
      }
      return feature
    })

    return features.filter((f) => !!f) as FeatureWithColor[]
  },
})

// Compare locations visits shapes
const compareLocationsVisitsShapes = selector<FeatureWithColor[][]>({
  key: key('compare-locations-visits-shapes'),
  get: async ({ get }) => {
    const _compareLocations = get(compareLocations)
    const compareVisits = get(compareLocationsVisits)
    const _boundaryType = get(boundaryType)
    const locations = get(compareLocations)

    const compareLocationsShapes: FeatureWithColor[][] = []

    for (let locationIndex = 0; locationIndex < compareVisits.length; locationIndex++) {
      const location = compareVisits[locationIndex]
      const ids = location.map((location) => location.id)
      const shapes = await getVisitsShapes(ids, _boundaryType)

      const features = shapes.map((shape, index) => {
        const feature: FeatureWithColor = {
          id: shape.id,
          geometry: shape.geometry,
          type: 'Feature',
          properties: {
            name: _compareLocations[locationIndex].name,
            value: compareVisits[locationIndex][index].percentTotal,
            locationIndex: locationIndex + 1,
            id: shape.id,
            fillColor: TTA_DEMO_COMPARE_COLORS[locationIndex + 1],
            lineColor: TTA_DEMO_COMPARE_COLORS[locationIndex + 1],
            locationId: locations[locationIndex].id,
          },
        }
        return feature
      })
      const compareLocationsShapesFiltered = features.filter((f) => !!f) as FeatureWithColor[]
      compareLocationsShapes.push(compareLocationsShapesFiltered)
    }
    return compareLocationsShapes
  },
})

// Primary + Compare locations visits shapes (when compare locations are selected)
export const allLocationsVisitsShapes = selector<FeatureWithColor[]>({
  key: key('all-locations-visits-shapes'),
  get: async ({ get }) => {
    const primaryShapes = get(locationVisitsShapes)
    const compareShapes = get(compareLocationsVisitsShapes)
    const percentFilter = get(shapesPercentageFilter)

    const filteredPrimaryShapes = primaryShapes.slice(0, Math.ceil(primaryShapes.length * percentFilter))
    const filteredCompareShapes = compareShapes
      .map((shapes) => shapes.slice(0, Math.ceil(shapes.length * percentFilter)))
      .flat()

    const combined = [...filteredPrimaryShapes, ...filteredCompareShapes]
    return combined
  },
})

export const allLocationsVisitsShapesWithOverlap = selector<FeatureWithColor[]>({
  key: key('all-locations-visits-shapes-with-overlap'),
  get: async ({ get }) => {
    const shapes = get(allLocationsVisitsShapes)

    const finalShapes = getShapesForMap(shapes)
    return finalShapes
  },
})

export const hasShapesWithOverlap = selector<boolean>({
  key: key('has-shapes-with-overlap'),
  get: async ({ get }) => {
    const shapes = get(allLocationsVisitsShapesWithOverlap)
    return shapes.some((shape) => !!shape.properties?.featuresProps)
  },
})

export const shapesCount = selector<number>({
  key: key('shapes-count'),
  get: async ({ get }) => {
    const shapes = get(locationVisitsShapes)
    return shapes.length
  },
})

export const noOverviewData = selector<boolean>({
  key: key('no-overview-data'),
  get: async ({ get }) => {
    const visits = get(locationVisitsMap(false))
    const demoData = get(ttareademographicsData)
    const population = demoData?.demographics?.['Overview']?.['Population']
    return (!demoData || (!population?.currentYear && !population?.fiveYear)) && Object.keys(visits).length === 0
  },
})

const getShapesForMap = (shapes: FeatureWithColor[]): FeatureWithColor[] => {
  const finalShapes: FeatureWithColor[] = [...shapes]
  const intersectedShapesMap: Record<string, FeatureWithColor> = {}

  for (let i = 0; i < shapes.length; i++) {
    const feature1 = shapes[i]
    if (!feature1) {
      continue
    }

    for (let j = i + 1; j < shapes.length; j++) {
      const feature2 = shapes[j]

      if (!feature1 || !feature2 || feature1.properties?.locationId === feature2.properties?.locationId) {
        continue
      }

      const intersectionExists = feature1.id === feature2.id
      if (!intersectionExists) {
        continue
      }

      const existingFeature = intersectedShapesMap[feature1.properties?.id]
      const fp1 = feature1.properties?.featuresProps ?? [feature1.properties]
      const fp2 = feature2.properties?.featuresProps ?? [feature2.properties]

      if (existingFeature) {
        const existingProps = existingFeature.properties?.featuresProps
        const fps = getUniqueProperties([...existingProps, ...fp1, ...fp2])
        const locationIndexes = getLocationIndexes(fps)
        const locationIds = getLocationsIds(fps)
        existingFeature.properties = {
          ...existingFeature.properties,
          locationIndex: locationIndexes,
          locationId: locationIds,
          featuresProps: fps,
        }
        intersectedShapesMap[feature1.properties?.id] = existingFeature
      } else {
        const fps = getUniqueProperties([...fp1, ...fp2])
        const locationIndexes = getLocationIndexes(fps)
        const locationIds = getLocationsIds(fps)

        const newFeature: FeatureWithColor = {
          geometry: feature1.geometry,
          id: feature1.properties?.id,
          type: feature1.type,
          properties: {
            id: feature1.properties?.id,
            fillColor: TTA_DEMO_OVERLAP_COLOR,
            lineColor: TTA_DEMO_OVERLAP_COLOR,
            locationIndex: locationIndexes,
            locationId: locationIds,
            featuresProps: fps,
          },
        }
        intersectedShapesMap[feature1.properties?.id] = newFeature
      }

      finalShapes[i] = null as any
      finalShapes[j] = null as any
    }
  }

  const intersectedShapes = Object.values(intersectedShapesMap)
  const allShapes = [...finalShapes, ...intersectedShapes].filter((shape) => !!shape)

  // Sort by visits DESC
  const sortedShapes = allShapes.sort((a, b) => {
    return (b.properties?.value || 0) - (a.properties?.value || 0)
  })
  return sortedShapes
}

const getUniqueProperties = (featuresProps: any[]): any[] => {
  return uniqBy(featuresProps, (x) => {
    return `${x.id}${x.locationId}`
  })
}

const getLocationIndexes = (featuresProps: any[]): string => {
  return featuresProps.map((f) => f.locationIndex).join('-')
}

const getLocationsIds = (featuresProps: any[]): string => {
  return featuresProps.map((f) => f.locationId).join('-')
}

export const createBuckets = (data: ExtendedShapeValue[]) => {
  const splits = [10, 10, 10, 10, 10, 10, 10, 10, 10, 10]
  const buckets: ExtendedShapeValue[][] = [[], [], [], [], [], [], [], [], [], []]
  const [...locations] = data
  const sortedLocations = locations.sort((locationA, locationB) => locationA.percentTotal - locationB.percentTotal)

  let bucket = 0
  sortedLocations.forEach((location) => {
    const bucketTotal = buckets[bucket].reduce((acc, curr) => curr.percentTotal + acc, 0)
    if (bucketTotal + location.percentTotal > splits[bucket] / 100) {
      bucket = Math.min(bucket + 1, 9)
    }
    buckets[bucket].push(location)
  })

  return buckets.reverse()
}

export const getDefaultShapeColors = (buckets: ExtendedShapeValue[][]) => (location?: ExtendedShapeValue) => {
  if (!location) {
    return {
      fillColor: [0, 0, 0, 0] as RGBAColor,
      lineColor: [0, 0, 0, 0] as RGBAColor,
      color: [0, 0, 0, 0] as RGBAColor,
    }
  }
  const locationBucketIndex = buckets.findIndex(
    (bucket) => bucket.findIndex((bucketedLocation) => bucketedLocation.id === location.id) !== -1
  ) as any

  const color = getMapColorsByIndex(locationBucketIndex || 0, 'mixed')
  return color
}

// Controls the top N percent of shapes to show on the map
export const shapesPercentageFilter = atom<number>({
  key: key('shape-percentage-filter'),
  default: 1,
})

// This allows the map shapes to update independently when the percentage filter changes
// and the demographic data is refetched after the user confirms the filter using this atom
// i.e. one to control what the map shows and this one to refetch the associated demographic data
// so as to not spam the API with requests but still allow the user to see the changes before confirming
export const demoDataPercentageFilter = atom<number>({
  key: key('demo-data-percentage-filter'),
  default: 1,
})

// Get the centers of the shapes
type CenterPointFeature = Feature<Point, { id: any; value: number }>
export const shapeCenters = selector<CenterPointFeature[]>({
  key: key('shape-centers'),
  get: ({ get }) => {
    const location = get(mobilityAtoms.alphamapLocation)
    if (!location) {
      return []
    }

    const shapes = get(allLocationsVisitsShapesWithOverlap)
    const features: CenterPointFeature[] = shapes.map((shape) => {
      const center = turf.centerOfMass(shape.geometry)
      return {
        type: 'Feature',
        geometry: center.geometry,
        properties: {
          id: shape.properties?.id,
          value: shape.properties?.value || 0,
          ...shape.properties,
        },
      } as CenterPointFeature
    })
    return features
  },
})

// Get the estimated trade area radius in miles (distance from the location center to the furthest shape)
export const estimatedTradeAreaRadiusMiles = selector<number | undefined>({
  key: key('estimated-trade-area-radius-miles'),
  get: ({ get }) => {
    const location = get(mobilityAtoms.alphamapLocation)
    if (!location) {
      return undefined
    }

    const shapes = get(shapeCenters)
    if (!shapes.length) {
      return undefined
    }

    // sort shapes by distance to location center descending
    const sortedShapes = [...shapes].sort((a, b) => {
      const distanceA = turf.distance(location.latLng, a.geometry, { units: 'miles' })
      const distanceB = turf.distance(location.latLng, b.geometry, { units: 'miles' })
      return distanceB - distanceA
    })

    const furthestShape = sortedShapes[0]
    const distance = turf.distance(location.latLng, furthestShape.geometry, { units: 'miles' })

    return distance < 1 ? parseFloat(distance.toFixed(2)) : Math.floor(distance)
  },
})

// Get a radius circle feature for the estimated trade area
export const estimatedTradeAreaRadiusFeature = selector<Feature<Polygon> | undefined>({
  key: key('estimated-trade-area-radius-feature'),
  get: ({ get }) => {
    const location = get(mobilityAtoms.alphamapLocation)
    const radius = get(estimatedTradeAreaRadiusMiles)
    if (!location || !radius) {
      return undefined
    }

    const circle = turf.circle(location.latLng, radius, {
      units: 'miles',
    })
    return circle
  },
})

// Calculate estimated trade area radius square miles
export const estTradeAreaRadiusSqMiles = selector<number | undefined>({
  key: key('est-trade-area-radius-sq-miles'),
  get: ({ get }) => {
    const radius = get(estimatedTradeAreaRadiusMiles)
    if (!radius) {
      return undefined
    }
    return Math.floor(Math.PI * radius * radius)
  },
})

// Calculate the trade area square miles
export const tradeAreaSqMiles = selector<number | undefined>({
  key: key('trade-area-sq-miles'),
  get: ({ get }) => {
    const shapes = get(allLocationsVisitsShapesWithOverlap)
    // get square miles of the shapes
    const squareMiles = shapes.reduce((acc, shape) => {
      const area = turf.area(shape.geometry)
      return acc + turf.convertArea(area, 'meters', 'miles')
    }, 0)
    return Math.floor(squareMiles)
  },
})
